/**
 * Werte der Navigation — Stufe 2.
 *
 * Sie sind global und nicht je Ausfahrt einstellbar: Sie beschreiben, wie mit
 * dem Straßennetz umgegangen wird, nicht wie eine Kolonne beurteilt wird. Alle
 * gemessenen Werte stammen aus den drei echten Fahrten vom 16. und 17.08.2026
 * — 5384 Spurpunkte über 95 Kilometer.
 */
export const ROUTING = Object.freeze({
    /**
     * Suchradius beim Einrasten der Spur auf das Straßennetz, in Metern.
     *
     * Valhallas Vorgabe sind 50 m, und mit ihr scheitert das Einrasten still:
     * Von der Fahrt am 16.08.2026 blieben statt 25 km ganze 228 Meter übrig, und
     * die Antwort sah dabei aus wie ein Erfolg. Mit 100 m rasten alle drei
     * Fahrten vollständig ein.
     */
    matchSearchRadiusM: 100,
    /**
     * Punkte, die weniger als diese Strecke auseinanderliegen, gelten dem
     * Einrasten als Zwischenpunkte und nicht als eigener Anhaltspunkt.
     *
     * Der zweite und wichtigere Teil derselben Beobachtung. Ein Spurpunkt
     * entsteht ab zehn Metern Bewegung (`trackMinMoveM`); in langsamer Fahrt
     * liegen sie deshalb dicht beieinander, und dicht liegende Punkte mit
     * GPS-Streuung führen den Abgleich in die Irre. Bei zwanzig Metern rastete
     * die Fahrt vom 16.08. von 14,5 auf 25,5 km ein, und der mittlere Abstand der
     * Rohpunkte zur eingerasteten Linie fiel von 149 m auf 1,6 m.
     *
     * Nach oben begrenzt: Bei fünfzig Metern schneidet das Einrasten Kurven ab —
     * gemessen 124 m größter Abstand statt 28 m.
     */
    matchInterpolationDistanceM: 20,
    /**
     * Zeitgrenze für eine Anfrage an den Routingdienst.
     *
     * Gemessen wurden 24 bis 44 Millisekunden für vollständige Tagesspuren von
     * 25 bis 40 Kilometern. Fünf Sekunden sind daher keine Wartezeit, sondern
     * eine Notbremse: Antwortet der Dienst so lange nicht, ist er nicht in
     * Ordnung, und der Fahrer soll das erfahren, statt ins Leere zu warten.
     */
    requestTimeoutMs: 5_000,
    /**
     * Wie weit hinter dem Wiedereinstieg der Rückweg endet, in Metern.
     *
     * Der Wiedereinstieg ist die Stelle der Spur, auf die das Fahrzeug gelotet
     * wurde. Endete der Rückweg genau dort, fiele das Abbiegen auf die Strecke
     * mit dem Ziel zusammen — und der Hinweis „links abbiegen" entstünde nie.
     * Sechzig Meter hinter der Einmündung ist das Abbiegen ein Manöver wie jedes
     * andere und steht in der Liste.
     */
    rejoinLookaheadM: 60,
    /**
     * Bis zu diesem Abstand zur Spur wird kein Rückweg berechnet.
     *
     * Wer auf der Strecke steht, hat keinen Weg zurück auf sie. Für ihn beginnt
     * die Navigation unmittelbar auf der Spur. Dreißig Meter sind zugleich die
     * Breite, ab der zwei stehende Fahrzeuge nicht mehr als beieinander gelten
     * (`MATCHING.togetherM`).
     */
    routeBackMinOffTrackM: 30,
    /**
     * Ab welchem Winkel zwischen Fahrtrichtung und Routenanfang „Bitte wenden"
     * gesagt wird.
     *
     * Der Routingdienst bekommt bewusst **keine** Fahrtrichtung übergeben. Er
     * darf deshalb in beide Richtungen losfahren und wählt die schnellere — am
     * 27.08.2026 gemessen: 143 s zurück auf die Spur statt 335 s einmal um den
     * Block, weil das Kostenmodell auf einer Landesstraße kein Wendemanöver
     * zulässt. Die Route ist damit richtig; nur ihr erster Satz war es nicht.
     * Er lautete „Auf Eilerner Straße Richtung Nordosten fahren", während das
     * Auto nach Südwesten stand.
     *
     * Hundertfünfzig Grad und nicht weniger: Ein Fahrzeug, das an einer
     * spitzwinkligen Einmündung steht, biegt scharf ab und wendet nicht — hieße
     * es dort „Bitte wenden", suchte der Fahrer eine Wendemöglichkeit, wo er nur
     * abbiegen müsste. Der umgekehrte Fehler ist wohlfeiler: Bleibt ein echtes
     * Wenden unbenannt, steht dort der Satz, der heute schon dort steht.
     */
    uturnBearingDeg: 150,
    /**
     * Höchstzahl Spurpunkte in einer Anfrage an das Einrasten.
     *
     * Liegt mehr dazwischen, wird ausgedünnt. Bei zwanzig Metern
     * Zwischenpunktabstand geht dabei nichts verloren, was das Einrasten
     * überhaupt auswertet. 3000 Punkte sind mehr als eine volle Tagesetappe.
     */
    maxTracePoints: 3_000,
    /**
     * Entfernung der Vorankündigung einer Abbiegung, in Metern.
     *
     * „In 300 Metern rechts abbiegen" — weit genug, um sich einzuordnen, nah
     * genug, dass es die richtige Abbiegung ist. Auf der Landstraße bei 80 km/h
     * sind vierhundert Meter achtzehn Sekunden; im Ort bei dreißig sind es
     * fast eine Minute, was zu früh wäre — deshalb folgt die zweite Ansage.
     */
    announceFarM: 400,
    /**
     * Entfernung der unmittelbaren Ansage, in Metern.
     *
     * „Rechts abbiegen" — jetzt. Achtzig Meter sind bei jedem Tempo, das in einer
     * Kolonne gefahren wird, spät genug, um eindeutig zu sein, und früh genug,
     * um noch zu blinken.
     */
    announceNearM: 80,
    /**
     * Ab dieser Reststrecke gilt die Kolonne als erreicht, in Metern.
     *
     * Die Navigation endet dann von selbst — sie soll nicht bis zur letzten
     * Wagenlänge weiterzählen, während der Fahrer längst wieder hinter dem
     * richtigen Auto herfährt. Hundertfünfzig Meter sind zugleich die Schwelle,
     * ab der ein Fahrzeug als abseits der Strecke gilt.
     */
    arrivalDistanceM: 150,
    /**
     * Takt, in dem eine laufende Navigation neu gerechnet wird.
     *
     * Die Führung fährt weiter, das Ziel wandert. Häufiger als alle fünf
     * Sekunden hat es keinen Sinn: Der Fahrer folgt derselben Straße, und eine
     * Hinweisliste, die unter der Hand wechselt, ist schlechter als eine, die
     * kurz alt ist.
     */
    recalcIntervalMs: 5_000,
});
//# sourceMappingURL=routing.js.map